import {useCallback, useEffect, useRef, useState} from "react";

export const PLAYBACK_SPEEDS = [0.5, 1, 2, 4, 8, 16];

export const useTimelineAnimation = (elapsedSeconds, setElapsedSeconds, secondsPerSecond = 1) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCropMode, setIsCropMode] = useState(false);
  const [cropRange, setCropRange] = useState([0, 300]);
  const [speedIndex, setSpeedIndex] = useState(1);
  const [stepSeconds, setStepSeconds] = useState(60);
  const requestRef = useRef();
  const previousTimeRef = useRef();

  const animate = useCallback((time) => {
    if (previousTimeRef.current !== undefined) {
      const deltaTime = (time - previousTimeRef.current) / 1000;
      const timeToAdd = deltaTime * PLAYBACK_SPEEDS[speedIndex] * secondsPerSecond;

      setElapsedSeconds(prev => {
        const nextTime = prev + timeToAdd;

        if (isCropMode && cropRange) {
          if (nextTime > cropRange[1] || nextTime < cropRange[0]) {
            return cropRange[0];
          }
        }

        return nextTime;
      });
    }

    previousTimeRef.current = time;

    if (isPlaying) {
      requestRef.current = requestAnimationFrame(animate);
    }
  }, [cropRange, isCropMode, isPlaying, secondsPerSecond, setElapsedSeconds, speedIndex]);

  useEffect(() => {
    if (isPlaying) {
      requestRef.current = requestAnimationFrame(animate);
    } else {
      cancelAnimationFrame(requestRef.current);
      previousTimeRef.current = undefined;
    }

    return () => cancelAnimationFrame(requestRef.current);
  }, [isPlaying, animate]);

  const togglePlay = () => setIsPlaying(prev => !prev);
  const pause = () => setIsPlaying(false);
  const stepForward = () => {
    setElapsedSeconds(prev => {
      const step = Number(stepSeconds);
      const nextTime = prev + step;

      if (isCropMode && cropRange) {
        if (nextTime > cropRange[1]) {
          return cropRange[0];
        }
      }

      return nextTime;
    });
  };

  const stepBackward = () => {
    setElapsedSeconds(prev => {
      const step = Number(stepSeconds);
      const nextTime = prev - step;

      if (isCropMode && cropRange) {
        if (nextTime < cropRange[0]) {
          return cropRange[1];
        }
      }

      return Math.max(0, nextTime);
    });
  };
  const restart = () => {
    setIsPlaying(false);
    setElapsedSeconds(0);
  };

  return {
    isPlaying,
    togglePlay,
    pause,
    isCropMode,
    toggleCropMode: () => setIsCropMode(prev => !prev),
    cropRange,
    setCropRange,
    speedIndex,
    setSpeedIndex,
    stepSeconds,
    setStepSeconds,
    stepForward,
    stepBackward,
    restart
  };
};
